import {
  Badge,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiArrowRightSLine } from "@remixicon/react";
import { useState } from "react";

import { useOrgsData } from "@/app/data/useOrgsData";
import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { IcoSearch } from "@/common/components/Icons";
import OrgLogo from "@/common/components/OrgLogo";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import pluralize from "@reflag/shared/utils/pluralize";

function ListOrgs() {
  const environment = useEnvironmentUnsafe();
  const { data: orgReq } = useOrgsData();
  const data = orgReq?.data ?? [];

  const [searchValue, setSearchValue] = useState<string>("");
  const searchIconColor = useColorModeValue("gray.400", "gray.600");

  const orgs =
    searchValue !== ""
      ? data.filter((i) =>
          i.name.toLowerCase().includes(searchValue.toLowerCase()),
        )
      : data;

  return (
    <>
      <InputGroup>
        <InputLeftElement
          color={!searchValue ? searchIconColor : undefined}
          pointerEvents="none"
        >
          <IcoSearch size="md" />
        </InputLeftElement>
        <Input
          autoComplete="off"
          placeholder="Search.."
          type="search"
          value={searchValue}
          autoFocus
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </InputGroup>
      <List my={4} spacing={2}>
        {orgs.map(({ id, name, logoUrl, apps, suspendedAt }) => (
          <ListItem
            key={id}
            alignItems="center"
            as={Button}
            display="flex"
            justifyContent="space-between"
            leftIcon={<OrgLogo mr={1} name={name} size="xs" url={logoUrl} />}
            pl={1.5}
            size="md"
            variant="subtle"
            width="full"
            onClick={() => environment?.setActiveOrg(id)}
          >
            <HStack flexGrow={1} spacing={4}>
              <Text fontWeight="semibold" isTruncated>
                {searchValue ? highlightStringMatch(name, searchValue) : name}
              </Text>
              <Text color="gray.500" fontSize="sm" fontWeight="normal">
                {`${apps.length} ${pluralize("app", apps.length)}`}
              </Text>
            </HStack>
            <HStack flexShrink={0}>
              {id === environment?.org?.id ? (
                <Badge colorScheme="brand">Current</Badge>
              ) : null}
              {suspendedAt !== null ? (
                <Badge colorScheme="red">Suspended</Badge>
              ) : null}
              <RiArrowRightSLine size={16} />
            </HStack>
          </ListItem>
        ))}
      </List>
    </>
  );
}

export default function OrganizationSwitcher({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal isOpen={isOpen} size="md" onClose={onClose}>
      <ModalOverlay />
      <ModalContent minH="xs" p={0}>
        <ModalCloseButton m={1} />
        <ModalBody display="flex" flexDirection="column" p={0}>
          <Heading fontSize="lg" fontWeight="semibold" mx={6} my={6}>
            Switch organization
          </Heading>
          <Divider />
          <Box p={4}>
            <ListOrgs />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
